/* tslint:disable:max-line-length */
import { EventEmitter, Injectable, Injector, OnDestroy, ViewRef } from '@angular/core';
import { MmFilterService } from '../mm-filter/mm-filter.service';
import { BehaviorSubject, firstValueFrom, from, iif, Observable, of, Subject } from 'rxjs';
import { Fault, LayoutFault, ViewFault } from '@docma-mm-core/fault/fault';
import { FieldFilter, MmFilterModel } from '../mm-filter/mm-filter.model';
import { Project } from '@docma-mm-core/project/project';
import { NotificationService } from '@mydocma/component-library/src/lib/notification';
import { Structure } from '@docma-mm-core/structure/structure';
import { ConnectivityService } from '@mydocma/core';
import { auditTime, filter, finalize, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FaultLayoutApiService } from '@docma-mm-core/fault/fault-layout.api.service';
import { Attachment, AttachmentSource } from '@docma-mm-core/attachment/attachment';
import { LoadStatusEnum } from '@docma-mm-core/model/load-state/load-state.enum';
import {
  FaultDataSearch,
  FaultDataSearchFilter,
  FaultPoolFilter,
  FaultPoolFilterMode
} from '@docma-mm-core/fault/fault-data';
import { LoadState } from '@docma-mm-core/model/load-state/load-state';
import { FaultService } from '@docma-mm-core/fault/fault.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AttachmentService } from '@docma-mm-core/attachment/attachment.service';
import { ChangeStatusEnum } from '@docma-mm-core/model/change-state/change-status.enum';
import { SyncStatusEnum } from '@docma-mm-core/model/sync-state/sync-status.enum';
import { AttachmentLoadingTypeEnum } from '@docma-mm-core/attachment/attachment-loading-type.enum';
import { SelectionService } from '@mydocma/component-library/src/lib/selection';
import { FilterCriteria } from '@docma-mm-core/filter/filter-criteria';
import { LayoutStateModel } from '../mm-filter/layout/layout-state.model';
import { MmViewService } from '../mm-view.service';
import { MmLayoutStateService } from '../mm-filter/layout/layout-state.service';
import { formatDateToYMD, isIdsEquals } from '@docma-mm-core/utils/utils';
import { FaultSortingSourceEnum } from '@docma-mm-core/fault/fault-sorting-source.enum';
import { MmProjectLoaderService } from '../mm-project/mm-project-loader.service';
import { AppContainerService, PendingService, PersonalizationService } from '@mydocma/app-container';
import { ActivityService } from '@docma-mm-core/activity/activity.service';
import { MmFaultsReportingService } from '../mm-reporting/faults/mm-faults-reporting.service';
import { MmReportTypeEnum } from '../mm-reporting/mm-report-type.enum';
import { MmFaultsCountService } from './mm-faults-view/services/mm-faults-count.service';
import { FaultSyncService, SyncStateChange } from '@docma-mm-core/fault/sync/fault-sync.service';
import { GroupSyncService } from '@docma-mm-core/group/group-sync.service';
import { MultiActivity } from '@docma-mm-core/activity/multi-activity';
import { Activity, ActivityData } from '@docma-mm-core/activity/activity';
import { ActivityPatchModeEnum } from '@docma-mm-core/activity/activity-patch-mode.enum';
import { CreatedOnFieldFormat, ModifiedOnFieldFormat } from '@docma-mm-core/fault/field-formats';
import { User } from '@docma-mm-core/user/user';
import { MmPinsLoaderService } from './mm-pins-loader.service';
import { BpPin } from '../bp-common/models/bp-pin';
import { ColDefsService } from './mm-faults-ag-grid/col-defs/col-defs.service';
import { PersonalizationKeysEnum } from '@docma-mm-core/personalization/personalization-keys.enum';
import { HeaderToggleMenuEnum } from './mm-faults-view/services/mm-faults-split.service';
import { MmProjectService } from '../mm-project/mm-project.service';
import { LoadLayoutFaultsOptions } from './load-layout-faults-options.enum';
import { MmTranslateService } from '../mm-translate.service';
import { MmRoutesEnum } from '../mm-routing/mm-routes.enum';
import { FieldsUserFilterService } from '../mm-filter/fields/fields-user-filter.service';
import { Router } from '@angular/router';
import { Logger } from '@mydocma/common/src/lib/logger';
import { Pool } from '../mm-common/pool/pool';
import { Field } from '@docma-mm-core/field/field';
import { FilterOperatorEnum } from '@docma-mm-core/filter/filter-operator.enum';
import { FilterLinkOperatorEnum } from '@docma-mm-core/filter/filter-link-operator.enum';
import { EMPTY_FILTER_MARKER, FIELD_DICT_MAPPING, FieldMappingEnum } from '../mm-filter/fields/field-mapping.enum';
import { DateFilterOptionsEnum } from '../mm-filter/fields/date-filter-options.enum';
import { FaultPersistenceService } from '@docma-mm-core/fault/fault-persistence.service';
import { PlatformModeStateService } from '../mm-offline/services/platform-mode-state.service';
import { Coordinate } from '@docma-mm-core/plan/plan';
import { environment } from 'src/environments/environment';
import { MmLoadedFaultsSortingService } from './mm-loaded-faults-sorting.service';
import { MmFaultsSortingService, SortSourceEnum, TileSortState } from './mm-faults-view/services/mm-faults-sorting.service';
import { MmOfflineFilterService } from '../mm-filter/mm-offline-filter.service';
import { MmProjectDownloadService } from '../mm-project/mm-download.service';
import { off } from 'hammerjs';
import {DocuWareIntegrationService} from '../docuware-integration.service';

@UntilDestroy()
@Injectable()
export class MmFaultsLoaderService implements OnDestroy {

  public get faultsCount() {
    return (this.faultsSubject.value?.loadedFaults?.length ?? 0);
  }

  public get allFaultsCount() {
    return (this.faultsSubject.value?.loadedFaults?.length ?? 0);
  }
  public get currentFault() {
    return this._currentFault;
  }
  public set currentFault(fault: ViewFault) {
    this._currentFault = fault;
  }

  private poolCountChangedSubject = new BehaviorSubject<{ poolId: number, newCount?: number }[]>(null);
  public poolCountChanged$ = this.poolCountChangedSubject.asObservable();

  private shouldHideFaultCallback: ((fault: ViewFault) => boolean) | null = null;
  private dwActive = false;

  constructor(
    private readonly filterService: MmFilterService,
    private readonly attachmentService: AttachmentService,
    private readonly layoutStateService: MmLayoutStateService,
    private readonly faultService: FaultService,
    private readonly faultDataService: FaultLayoutApiService,
    private readonly connectivityService: ConnectivityService,
    private readonly notificationService: NotificationService,
    private readonly viewService: MmViewService,
    private readonly translateService: MmTranslateService,
    private readonly pendingService: PendingService,
    private readonly selectionService: SelectionService<ViewFault>,
    private readonly projectLoaderService: MmProjectLoaderService,
    public readonly mmFaultsCountService: MmFaultsCountService,
    private readonly mmFaultsReportingService: MmFaultsReportingService,
    private readonly activityService: ActivityService,
    private readonly groupSyncService: GroupSyncService,
    private readonly faultSyncService: FaultSyncService,
    private readonly pinsLoaderService: MmPinsLoaderService,
    private readonly colDefsService: ColDefsService,
    private readonly personalizationService: PersonalizationService,
    private readonly appContainerService: AppContainerService,
    private readonly mmProjectService: MmProjectService,
    private readonly router: Router,
    private readonly injector: Injector,
    private readonly platformModeStateService: PlatformModeStateService,
    private readonly persistenceService: FaultPersistenceService,
    private readonly loadedFaultsSortingService: MmLoadedFaultsSortingService,
    private readonly faultsSortingService: MmFaultsSortingService,
    private readonly mmOfflineFilterService: MmOfflineFilterService,
    private readonly downloadService: MmProjectDownloadService,
    private readonly docuWareIntegrationService: DocuWareIntegrationService,
    private readonly logger: Logger) {

    this.docuWareIntegrationService.enabled$
      .pipe(untilDestroyed(this))
      .subscribe(enabled => {
        this.dwActive = enabled;
      });

    this.pendingService.registerPendingState(this.loadingFaultsSubject);
    this.pendingService.registerPendingState(this.loadingLayoutFaultsSubject);

    /* IMMEDIATE UNLOAD DATA OF PREVIOUS PROJECT */
    this.mmProjectService.loadingState
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe(() => {
        this.onProjectChanged();
      });

    this.connectivityService.appIsInOfflineMode
      .pipe(
        untilDestroyed(this),
        tap(o => this.onOfflineChanged(o)),
        switchMap(o => this.viewService.currentProject$.pipe(filter(p => this.onCurrentProjectChanged(p)))),
        switchMap(p => this.pinsLoaderService.loadPins(p)),
        switchMap(p => this.filterService.state),
        switchMap(fm => this.layoutStateService.currentLayoutState$.pipe(map(ls => ({ ls, fm })))),
        auditTime(50),
        switchMap(f => this.loadingSubject.pipe(filter(l => !l && this.shouldReloadFaults(f.fm, f.ls.state)), map(l => f)))
      )
      .subscribe(async f => {
        const regex = new RegExp(`^\/${MmRoutesEnum.customers}`, 'i');
        if (regex.test(this.router.url)) {
          this.isRefreshPending = true;
          return;
        }

        const loadOptions = f.ls.loadOptions;
        f.ls.loadOptions = LoadLayoutFaultsOptions.None;
        await this.reloadAllFaults(f.fm, f.ls.state, false, false, loadOptions);
      });

    this.faultSyncService.syncFaultsState$
      .pipe(untilDestroyed(this))
      .subscribe(params => {
        this.syncStateChanges = [];
        for (const syncStateChange of params.changes) {
          this.syncStateChanges[syncStateChange.faultUid] = syncStateChange;
        }
      });

    this.faultService.onFaultChanged
      .pipe(untilDestroyed(this))
      .subscribe(async f => this.faultChanged(f));

    this.faultService.onFaultDeleted
      .pipe(untilDestroyed(this))
      .subscribe(async f => this.removeFaults([f]));

    this.activityService.onActivityDeleted
      .pipe(untilDestroyed(this))
      .subscribe(async args => {
        this.removeGroupFaultActivites(args.activity, args.groupDelete, args.modifiedOn, args.modifier);
      });

    this.attachmentService.onAttachmentsAdded
      .pipe(untilDestroyed(this))
      .subscribe(args => {
        this.AddActivityAttachments(args.activityAttachments, args.activity);
      });

    this.attachmentService.onAttachmentChanged
      .pipe(untilDestroyed(this))
      .subscribe(args => {
        this.ChangeActivityAttachment(args.attachment, args.activity);
      });

    this.attachmentService.onAttachmentDeleted
      .pipe(untilDestroyed(this))
      .subscribe(args => {
        this.DeleteActivityAttachment(args.attachment, args.activity, args.modifiedOn, args.modifier);
        this.attachmentDeletedSubject.next(args);
      });


    this.mmFaultsReportingService.onReportReady
      .pipe(
        untilDestroyed(this),
        filter(r => r.reportType === MmReportTypeEnum.writing && r.projectId === this.project?.MyDocmaUid)
      )
      .subscribe(async reportTask => {
        if (reportTask.faults && reportTask.faults.length > 0) {
          const faultsToReload = reportTask.faults.map(fd => this.loadedFaults.find(lf => lf.uid === fd.UniqueId));
          if (faultsToReload.length > 0) {
            (await this.loadLayoutFaults(faultsToReload.filter(vf => vf.layoutState?.loadState?.status !== LoadStatusEnum.NotLoaded), true)).subscribe();
            await this.loadFaults(faultsToReload.filter(vf => vf.loadState?.status !== LoadStatusEnum.NotLoaded), true).toPromise();
          }
        }
      });

    this.groupSyncService.multiActivitySynced$
      .pipe(untilDestroyed(this))
      .subscribe(multiActivityData => {
        if (multiActivityData.FaultActivities) {
          multiActivityData.FaultActivities.map(async fa => {
            const fault = this.loadedFaults.find(f => f.uid === fa.Fault.UniqueId);
            if (fault) {
              const activity = fault.activities.find(a => a.uid === fa.Activity.UniqueId);
              if (activity && !activity.id && activity.id !== fa.Activity.Id) {
                activity.id = fa.Activity.Id;
              }
            }
          });
        }
      });

    // this.faultChangesCommitService.activityEditCommitted$
    //   .pipe(untilDestroyed(this))
    //   .subscribe(params => {
    //     // if (params.activityEdit.changes && params.activityEdit.activities) {
    //     //   params.activityEdit.activities.map(a => {
    //     //     const fault = this.loadedFaults.find(f => f.uid === a.fault.uid);
    //     //     if (fault) {
    //     //       const faultActivity = fault.activities.find(act => act.uid === a.uid);
    //     //       if(faultActivity) {
    //     //         Object.keys(params.activityEdit.changes).forEach(prp => {
    //     //           faultActivity.data[prp] = params.activityEdit.changes[prp];
    //     //         });
    //     //       }
    //     //     }
    //     //   });
    //     // }
    //   });
    //
    // this.groupSyncService.activityEditSynced$
    //   .pipe(untilDestroyed(this))
    //   .subscribe(activityEditData => {
    //     if (activityEditData.patchData && activityEditData.activities) {
    //       activityEditData.activities.map(a => {
    //         const fault = this.loadedFaults.find(f => f.uid === a.Fault.UniqueId);
    //         if (fault) {
    //           const faultActivity = fault.activities.find(act => act.uid === a.UniqueId);
    //           if(faultActivity) {
    //             Object.keys(activityEditData.patchData).forEach(prp => {
    //               if (faultActivity.data[prp] !== activityEditData.patchData[prp]) {
    //                 faultActivity.data[prp] = activityEditData.patchData[prp];
    //               }
    //             });
    //           }
    //         }
    //       });
    //     }
    //
    //     if (multiActivityData.FaultActivities) {
    //       multiActivityData.FaultActivities.map(async fa => {
    //         const fault = this.loadedFaults.find(f => f.uid === fa.Fault.UniqueId);
    //         if (fault) {
    //           const activity = fault.activities.find(a => a.uid === fa.Activity.UniqueId);
    //           if (activity && !activity.id && activity.id !== fa.Activity.Id) {
    //             activity.id = fa.Activity.Id;
    //           }
    //         }
    //       });
    //     }
    //
    //   });

    this.faultsSortingService.sortState$
      .pipe(
        untilDestroyed(this),
        filter(sortState => sortState.source === SortSourceEnum.Tile && !!sortState.timestamp)
      )
      .subscribe(async (sortState) => {
        if (this.loadedFaults?.length > 0 && (this.platformModeStateService.isLocalMode || this.offline)) {
          await this.applyLocalSorting(sortState);
          this.faultsSubject.next({ loadedFaults: this.loadedFaults, loadOptions: LoadLayoutFaultsOptions.None });
        }
      });
  }

  isRefreshPending = false;

  private filter: MmFilterModel;
  private faultsSubject = new BehaviorSubject<{ loadedFaults: ViewFault[], loadOptions?: LoadLayoutFaultsOptions, projectChanged?: boolean }>({ loadedFaults: [], loadOptions: LoadLayoutFaultsOptions.None, projectChanged: false });
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private layoutFaultSubject = new Subject<ViewFault>();
  private layoutFaultsSubject = new Subject<ViewFault[]>();

  private loadLayoutFaultsOptionsSubject = new BehaviorSubject<LoadLayoutFaultsOptions>(LoadLayoutFaultsOptions.None);
  public loadLayoutFaultsOptions$ = this.loadLayoutFaultsOptionsSubject.asObservable();

  private onLayoutStateChanged = new Subject<boolean>();
  private projectStructures: Structure[];
  private offline = false;
  private forceLayoutReload = false;
  private loadedFaults: ViewFault[] = []; // update
  private project: Project;
  private layoutState: LayoutStateModel;
  private previousPoolExclusionFilters: Pool[] = [];
  private previousPoolInclusionFilters: Pool[] = [];
  private previousFieldFilters: FieldFilter[] = [];

  private loadingLayoutFaultsSubject = new BehaviorSubject<boolean>(false);
  private loadingFaultsSubject = new BehaviorSubject<boolean>(false);

  loadingLayoutFaults$ = this.loadingLayoutFaultsSubject.asObservable();

  public faults = this.faultsSubject.asObservable();

  public onFaultAdded = new EventEmitter<ViewFault>();
  public onFaultsRemoved = new EventEmitter<ViewFault[]>();

  public loadingState = this.loadingSubject.asObservable();

  public onFaultLoading = this.faultService.onFaultLoading;
  public onFaultLoaded = this.faultService.onFaultLoaded;
  public onLayoutFaultLoaded = this.layoutFaultSubject.asObservable();
  public onLayoutFaultsLoaded = this.layoutFaultsSubject.asObservable();

  activitiesDeletedSubject = new Subject<Activity[]>();
  public onActivitiesDeleted = this.activitiesDeletedSubject.asObservable();

  private faultChangedSubject = new Subject<Fault>();
  onFaultChanged = this.faultChangedSubject.asObservable();

  private attachmentDeletedSubject = new Subject<{ attachment: Attachment, fault: Fault, activity: Activity, modifiedOn: Date, modifier: User }>();
  onAttachmentDeleted = this.attachmentDeletedSubject.asObservable();

  private syncStateChanges: { [faultUid: string]: SyncStateChange }[];
  private _currentFault: ViewFault;

  public toggledLeftViews = [];

  private faultsLoadedGridNoFieldsCase = false;

  onFaultRefreshed = new Subject<ViewFault>();

  onFaultIdsLoadingSubject = new BehaviorSubject<boolean>(false);
  onFaultIdsLoading$ = this.onFaultIdsLoadingSubject.asObservable();

  public async refreshFaultsByOverview() {
    if (this.isRefreshPending) {
      this.isRefreshPending = false;
      const fs = await firstValueFrom(this.filterService.state);
      const ls = await firstValueFrom(this.layoutStateService.currentLayoutState$);

      const loadOptions = ls.loadOptions;
      ls.loadOptions = LoadLayoutFaultsOptions.None;
      await this.reloadAllFaults(fs, ls.state, false, false, loadOptions);
    }
  }

  public async refreshFaults(loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {
    if (!this.faultsLoadedGridNoFieldsCase) {
      return;
    }

    const filterState = await this.filterService.state.pipe(first()).toPromise();
    const layoutState = await this.layoutStateService.currentLayoutState$.pipe(first()).toPromise();
    await this.reloadAllFaults(filterState, layoutState.state, false, true, loadOptions);
  }

  public async reloadAllFaults(filterModel: MmFilterModel, layoutState: LayoutStateModel, reloadFaultDetails: boolean = false, refreshByTileView: boolean = false, loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {
    if (this.loadingSubject.value) {
      console.log('skipping reloadAllFaults - already loading');
      return;
    }

    if (!this.project || !this.filter || !this.layoutState) {
      return;
    }

    this.loadingSubject.next(true);
    try {
      this.onLayoutStateChanged.next(true);
      await this.loadAllFaultIds(this.project, this.filter?.structure, this.layoutState, reloadFaultDetails, refreshByTileView, loadOptions);
      this.mmFaultsCountService.removeDeletedFaults(false);
      this.mmFaultsCountService.removeNewFaults(undefined, false);

      let faultCount;
      if (this.platformModeStateService.isLocalMode) {
        faultCount = await this.persistenceService.getFaultCount(this.project.MyDocmaUid);
      }
      else {
        faultCount = await firstValueFrom(this.faultDataService.getAllFaultCount(filterModel.structure.RootId, layoutState.layout.Id, layoutState.layout.FilterDisabled));
      }

      this.mmFaultsCountService.setAllFaultsCount(faultCount);

    } catch (err) {
      if (this.connectivityService.isOfflineVal) {
        this.logger.warn('reloadAllFaults failed — device went offline during request');
      } else {
        this.logger.error(err, '', 'MmFaultsLoaderService');
        this.notificationService.error(await this.translateService.get('MM.fault.loadError').toPromise());
      }
    }
    this.loadingSubject.next(false);
  }

  ngOnDestroy(): void {
    this.pendingService.removePendingState(this.loadingFaultsSubject);
    this.pendingService.removePendingState(this.loadingLayoutFaultsSubject);
  }

  public shouldReloadFaults(filterModel: MmFilterModel, layoutStateModel: LayoutStateModel): boolean {
    if (!filterModel?.project || !layoutStateModel?.project || filterModel.project.MyDocmaUid !== layoutStateModel.project.MyDocmaUid) {
      return false;
    }
    const isProjectStructureChanged = this.isProjectStructureChanged(filterModel);
    const isLayoutStateChanged = this.isLayoutStateChanged(layoutStateModel);
    const isPoolExclusionFiltersChanged = this.isPoolExclusionFiltersChanged(filterModel);
    const isPoolInclusionFiltersChanged = this.isPoolInclusionFiltersChanged(filterModel);
    const isFieldFiltersChanged = this.isFieldFiltersChanged(filterModel);
    const shouldReload = (isProjectStructureChanged || isLayoutStateChanged || isPoolExclusionFiltersChanged || isPoolInclusionFiltersChanged || isFieldFiltersChanged) && !!layoutStateModel?.layout?.Id;
    return shouldReload;
  }

  private async faultChanged(fault: Fault) {
    const found = this.loadedFaults.find(f => (fault.id && fault.id === f.id) || (fault.uid && fault.uid === f.uid));
    if (found) {
      /* currently nothing to do here... */
    } else {
      if (fault.changeState.status === ChangeStatusEnum.New) {
        if (fault instanceof ViewFault) {
          const shouldHide = this.shouldHideFaultCallback ? this.shouldHideFaultCallback(fault as ViewFault) : false;

          if (!shouldHide) {
            this.loadedFaults.push(fault as ViewFault);
            this.faultsSubject.value.loadedFaults.push(fault as ViewFault);
            if (this.hasFaultFilterTags(fault)) {
              this.onFaultAdded.emit(fault);
            }
          } else {
            console.warn('faultChanged: Hiding new fault from view due to pool filter rejection', fault.uid);
          }
        }
      }
    }
  }

  public async removeFaults(faults: Fault[], discardByUid: boolean = false) {
    const removedFaults: ViewFault[] = [];
    let removedNewFaults = 0;

    faults.forEach(fault => {
      const foundIndex = this.loadedFaults.findIndex(f => (fault.id && fault.id === f.id) || (fault.uid && fault.uid === f.uid));
      if (foundIndex > -1) {
        this.loadedFaults.splice(foundIndex, 1);
        const foundIndex2 = this.faultsSubject.value.loadedFaults.findIndex(f => (fault.id && fault.id === f.id) || (fault.uid && fault.uid === f.uid));
        if (foundIndex2 > -1) {
          removedFaults.push(this.faultsSubject.value.loadedFaults[foundIndex2]);

          if (!!this.faultsSubject.value[foundIndex2]?.unsavedNew) {
            removedNewFaults++;
          }
          this.faultsSubject.value.loadedFaults.splice(foundIndex2, 1);
        }
      }
    });

    if (removedFaults.length > 0) {

      if (discardByUid) {
        removedFaults.forEach(x => x.id = undefined);
      }

      this.onFaultsRemoved.emit(removedFaults);
      this.mmFaultsCountService.removeNewFaults(removedNewFaults);
    }
  }

  async addFault(fault: ViewFault) {

    const shouldHide = this.shouldHideFaultCallback ? this.shouldHideFaultCallback(fault) : false;

    if (shouldHide) {
      console.warn('addFault: Hiding new fault from view due to pool filter rejection', fault.uid);
      return;
    }

    const viewFault = fault;
    if (fault.data.Pin) {
      const pin: BpPin = { ...fault.data.Pin, ...{ ExternalId: fault.uid } };
      fault.pin = this.pinsLoaderService.convertToCoordinate(pin);
      fault.bpPin = pin;
      this.pinsLoaderService.addPin(pin);
    }

    this.loadedFaults.splice(0, 0, viewFault);
    this.faultsSubject.value.loadedFaults.splice(0, 0, viewFault);

    this.onFaultAdded.emit(viewFault);

    this.mmFaultsCountService.addNewFaults(1);
  }

  getFault(faultUid: string): ViewFault {
    return this.loadedFaults.find(f => f.uid === faultUid);
  }

  getFaults(faultUids: string[]): ViewFault[] {
    return this.loadedFaults.filter(f => faultUids.findIndex(x => x === f.uid) !== -1);
  }

  private onOfflineChanged(offline: boolean) {
    if (this.offline !== offline) {
      this.offline = offline;
      this.forceLayoutReload = true;
    }
  }

  private onProjectChanged() {
    this.project = null;
    this.filter = null;
    this.previousPoolExclusionFilters = [];
    this.previousPoolInclusionFilters = [];
    this.previousFieldFilters = [];
    this.selectionService.stopSelectionMode();
    this.faultsSubject.next({ loadedFaults: [], loadOptions: LoadLayoutFaultsOptions.None, projectChanged: true });
    return true;
  }

  private onCurrentProjectChanged(p: Project): boolean {
    this.project = p;
    // this.filter = null;
    // this.selectionService.stopSelectionMode();
    // this.faultsSubject.next([]);
    return true;
  }

  private isProjectStructureChanged(f: MmFilterModel): boolean {

    if (!this.project || !f || !f.project || f.project.MyDocmaUid !== this.project.MyDocmaUid) {
      return false;
    }
    if (isIdsEquals(this.filter && this.filter.structure, f.structure)) {
      return false;
    }
    this.filter = f;
    return true;
  }

  private isLayoutStateChanged(ls: LayoutStateModel): boolean {

    if (this.forceLayoutReload) {
      this.forceLayoutReload = false;
      return true;
    } else {
      if (!this.project || !ls || !ls.layout || !ls.project || ls.project.MyDocmaUid !== this.project.MyDocmaUid) {
        return false;
      }
      let isLayoutChanged = !isIdsEquals(this.layoutState && this.layoutState.layout, ls.layout);
      if (isLayoutChanged) {
        this.selectionService.stopSelectionMode();
      } else {
        // TODO: Check Filters/Layouts
        // isLayoutChanged = this.layoutState?.filters?.length > 0 || ls?.filters?.length > 0 ||
        // this.layoutState?.sortItems?.length > 0 || ls?.sortItems?.length > 0;
        isLayoutChanged = this.layoutState.id !== ls.id;
      }

      this.layoutState = ls;
      return isLayoutChanged;
    }
  }

  private isPoolExclusionFiltersChanged(filterModel: MmFilterModel): boolean {
    const currentFilters = filterModel.poolExclusionFilters || [];
    const previousFilters = this.previousPoolExclusionFilters || [];

    if (currentFilters.length !== previousFilters.length) {
      this.previousPoolExclusionFilters = [...currentFilters];
      return true;
    }

    const currentIds = currentFilters.map(p => p.Id).sort();
    const previousIds = previousFilters.map(p => p.Id).sort();

    const hasChanged = !currentIds.every((id, index) => id === previousIds[index]);

    if (hasChanged) {
      this.previousPoolExclusionFilters = [...currentFilters];
    }

    return hasChanged;
  }

  private isPoolInclusionFiltersChanged(filterModel: MmFilterModel): boolean {
    const currentFilters = filterModel.poolInclusionFilters || [];
    const previousFilters = this.previousPoolInclusionFilters || [];

    if (currentFilters.length !== previousFilters.length) {
      this.previousPoolInclusionFilters = [...currentFilters];
      return true;
    }

    const currentIds = currentFilters.map(p => p.Id).sort();
    const previousIds = previousFilters.map(p => p.Id).sort();

    const hasChanged = !currentIds.every((id, index) => id === previousIds[index]);

    if (hasChanged) {
      this.previousPoolInclusionFilters = [...currentFilters];
    }

    return hasChanged;
  }

  private isFieldFiltersChanged(filterModel: MmFilterModel): boolean {
    const currentFilters = filterModel.fieldFilters || [];
    const previousFilters = this.previousFieldFilters || [];

    if (currentFilters.length !== previousFilters.length) {
      this.previousFieldFilters = [...currentFilters];
      return true;
    }

    const hasChanged = !currentFilters.every(current => {
      const previous = previousFilters.find(p => p.fieldName === current.fieldName);
      return previous && previous.value === current.value;
    });

    if (hasChanged) {
      this.previousFieldFilters = [...currentFilters];
    }

    return hasChanged;
  }

  private reset() {
    this.faultsSubject.next({ loadedFaults: [], loadOptions: LoadLayoutFaultsOptions.None });
  }

  private async getApiSearchParams(structureId: number, layoutState: LayoutStateModel, onlySearchIds: boolean, setSorting: boolean = false, setFilter: boolean = true) {
    const fieldCriteriaFormats: { [fieldFormat: string]: any } = {};
    fieldCriteriaFormats.CreatedOnFieldFormat = CreatedOnFieldFormat.Date;
    fieldCriteriaFormats.ModifiedOnFieldFormat = ModifiedOnFieldFormat.Date;
    fieldCriteriaFormats.CreatedOnMobileFieldFormat = CreatedOnFieldFormat.Date;
    fieldCriteriaFormats.ModifiedOnMobileFieldFormat = ModifiedOnFieldFormat.Date;

    const searchParams = new FaultDataSearch({
      Structure: structureId.toString(),
      LayoutId: layoutState.layout.Id,
      OnlyDefectId: true,
      FieldCriteriaFormats: fieldCriteriaFormats
    });

    const currentFilter = await firstValueFrom(this.filterService.state);

    const poolFilters: FaultPoolFilter[] = [];

    if (currentFilter?.poolExclusionFilters && currentFilter.poolExclusionFilters.length > 0) {
      poolFilters.push(...currentFilter.poolExclusionFilters.map(pool => new FaultPoolFilter({
        PoolId: pool.Id,
        Mode: FaultPoolFilterMode.Exclude
      })));
    }

    if (currentFilter?.poolInclusionFilters && currentFilter.poolInclusionFilters.length > 0) {
      poolFilters.push(...currentFilter.poolInclusionFilters.map(pool => new FaultPoolFilter({
        PoolId: pool.Id,
        Mode: FaultPoolFilterMode.Include
      })));
    }


    if (poolFilters.length > 0) {
      searchParams.FaultPoolFilters = poolFilters;
    }

    if (layoutState.layout.FilterDisabled !== undefined) {
      searchParams.IsLayoutFilterDisabled = layoutState.layout.FilterDisabled;
    }

    if (setFilter) {
      const searchFilter = new FaultDataSearchFilter();
      let layoutFilterCriterias: FilterCriteria[] = [];

      const maxSearchFiltersId = (!layoutState.searchFilters ? 0 : Math.max(... (layoutState.searchFilters ?? []).map(x => x.Id)));

      if (layoutState.filters && layoutState.filters.length > 0) {
        layoutFilterCriterias = layoutState.filters.map(f => {

          const crit = new FilterCriteria(f);
          crit.Id = crit.Id + maxSearchFiltersId;
          if (crit.ParentId != null && crit.ParentId !== -Infinity) {
            crit.ParentId = crit.ParentId + maxSearchFiltersId;
          }
          return crit;
        });
      }

      const parentId = layoutFilterCriterias.find(x => !x.ParentId)?.Id;
      let filterId = Math.max(...layoutFilterCriterias.map(x => x.Id));
      if (!parentId && filterId === -Infinity) {
        filterId = 0;
      }

      const fieldDict = Object.fromEntries(await firstValueFrom(this.projectLoaderService.fields
        .pipe(
          filter(userPerm => userPerm.loadState.status === LoadStatusEnum.Loaded),
          map(m => {
            return m.data.filter(x => x.Id != null).map(x => [x.Id.toString(), x]);
          }),
        )));



      /* TEST-CODE NEW FIELD FILTERS */
      let maxFilterId = Math.max(...[...(layoutState.searchFilters ?? []).map(x => x.Id),
      ...(layoutState.filters ?? []).map(x => x.Id)]);

      if (maxFilterId === -Infinity) {
        maxFilterId = 0;
      }

      // MMW-4039
      // const filterField = new Field({Id: +fieldDict.Company_Name1.FieldId, DefaultName: fieldDict.Company_Name1.Id});
      // const value = '79';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.Craft_Name1.FieldId, DefaultName: fieldDict.Craft_Name1.Id});
      // const value = '52';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.Order_Name1.FieldId, DefaultName: fieldDict.Order_Name1.Id});
      // const value = '205'
      // const logicalOperator = FilterOperatorEnum.Equal;

      // MMW-4034
      // const filterField = new Field({Id: +fieldDict.CreatedOn.FieldId, DefaultName: fieldDict.CreatedOn.Id});
      // const value = '2025-07-01';
      // const logicalOperator = FilterOperatorEnum.GreaterEqual;
      // const value = '2025-06-26';
      // const value2 = '2025-06-27';
      // const logicalOperator = FilterOperatorEnum.Between;

      // MMW-4035
      // const filterField = new Field({Id: +fieldDict.Creator.FieldId, DefaultName: fieldDict.Creator.Id});
      // const value = '49';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.Modifier.FieldId, DefaultName: fieldDict.Modifier.Id});
      // const value = '49';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // MMW-4037
      // const filterField = new Field({Id: +fieldDict.Attribute_Name1.FieldId, DefaultName: fieldDict.Attribute_Name1.Id});
      // const value = '2';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.AttributeGroup_Name1.FieldId, DefaultName: fieldDict.AttributeGroup_Name1.Id});
      // const value = '1';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // MMW-4038
      // const filterField = new Field({Id: +fieldDict.ResponsibleCO.FieldId, DefaultName: fieldDict.ResponsibleCO.Id});
      // const value = '149';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.ResponsibleCO_Company.FieldId, DefaultName: fieldDict.ResponsibleCO_Company.Id});
      // const value = '172';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.ResponsibleCT.FieldId, DefaultName: fieldDict.ResponsibleCT.Id});
      // const value = '149';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.ResponsibleCT_Company.FieldId, DefaultName: fieldDict.ResponsibleCT_Company.Id});
      // const value = '172';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.ResponsibleSC.FieldId, DefaultName: fieldDict.ResponsibleSC.Id});
      // const value = '149';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.ResponsibleSC_Company.FieldId, DefaultName: fieldDict.ResponsibleSC_Company.Id});
      // const value = '172';
      // const logicalOperator = FilterOperatorEnum.Equal;


      // MMW-4036
      // const filterField = new Field({Id: +fieldDict.LastActivitySC_StatusGroupSC.FieldId, DefaultName: fieldDict.LastActivitySC_StatusGroupSC.Id});
      // const value = '2';  /* '1' = 'Offen', '2' = 'Abgeschlossen' */
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.LastActivityCO_StatusGroupSC.FieldId, DefaultName: fieldDict.LastActivityCO_StatusGroupSC.Id});
      // const value = '2';  /* '1' = 'Offen', '2' = 'Abgeschlossen' */
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.LastActivityCO_StatusGroupCT.FieldId, DefaultName: fieldDict.LastActivityCO_StatusGroupCT.Id});
      // const value = '2';  /* '1' = 'Offen', '2' = 'Abgeschlossen' */
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.LastActivityCT_StatusGroupCT.FieldId, DefaultName: fieldDict.LastActivityCT_StatusGroupCT.Id});
      // const value = '2';  /* '1' = 'Offen', '2' = 'Abgeschlossen' */
      // const logicalOperator = FilterOperatorEnum.Equal;


      // MMW-4040
      // const filterField = new Field({Id: +fieldDict.LastActivityCT_Status_Name1.FieldId, DefaultName: fieldDict.LastActivityCT_Status_Name1.Id});
      // const value = '61';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.LastActivitySC_Status_Name1.FieldId, DefaultName: fieldDict.LastActivitySC_Status_Name1.Id});
      // const value = '55';
      // const logicalOperator = FilterOperatorEnum.Equal;


      // MMW-4041
      // const filterField = new Field({Id: +fieldDict.DelayGroupSC_Id.FieldId, DefaultName: fieldDict.DelayGroupSC_Id.Id});
      // const value = '7';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.DelayGroupCT_Id.FieldId, DefaultName: fieldDict.DelayGroupCT_Id.Id});
      // const value = '7';
      // const logicalOperator = FilterOperatorEnum.Equal;


      // MMW-4068
      // TO DO - confirm with Stephan that this is the correct format
      // const filterField = new Field({Id: +fieldDict.Classification1_Name1.FieldId, DefaultName: fieldDict.Classification1_Name1.Id});
      // const value = '1';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.Classification2_Name1.FieldId, DefaultName: fieldDict.Classification2_Name1.Id});
      // const value = '2';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.Classification3_Name1.FieldId, DefaultName: fieldDict.Classification3_Name1.Id});
      // const value = '9';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.Classification4_Name1.FieldId, DefaultName: fieldDict.Classification4_Name1.Id});
      // const value = '10';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // MMW-4069
      // TO DO - confirm with Stephan that this is the correct format
      // const filterField = new Field({Id: +fieldDict.ProjectSpecific_Name1.FieldId, DefaultName: fieldDict.ProjectSpecific_Name1.Id});
      // const value = '1';
      // const logicalOperator = FilterOperatorEnum.Equal;

      // const filterField = new Field({Id: +fieldDict.ProjectSpecific2_Name1.FieldId, DefaultName: fieldDict.ProjectSpecific2_Name1.Id});
      // const value = '2';
      // const logicalOperator = FilterOperatorEnum.Equal;


      // const companyFilter = new FilterCriteria();
      // companyFilter.Id = maxFilterId + 1;
      // companyFilter.Field = filterField;
      // companyFilter.LogicalOperator = logicalOperator;
      // companyFilter.LinkOperator = FilterLinkOperatorEnum.And;
      // companyFilter.ComparativeValue = value;
      // companyFilter.ComparativeValue2 = value2;




      /* FIELD FILTERS IMPLEMENTATION */
      const fieldFilterCriterias: FilterCriteria[] = [];
      if (currentFilter.fieldFilters && currentFilter.fieldFilters.length > 0) {
        let fieldFilterId = maxFilterId;

        currentFilter.fieldFilters.forEach(fieldFilter => {
          const fieldDictKey = this.getFieldDictKey(fieldFilter.fieldName);
          const fieldInfo = fieldDict[fieldDictKey];
          if (fieldInfo) {
            const filterField = new Field({
              Id: +fieldInfo.FieldId,
              DefaultName: fieldInfo.Id
            });

            if (fieldFilter.fieldName === FieldMappingEnum.CreatedOn ||
              fieldFilter.fieldName === FieldMappingEnum.ModifiedOn) {
              const dateFilters = this.createDateFilterCriterias(fieldFilter, filterField, fieldFilterId + 1);
              fieldFilterCriterias.push(...dateFilters);
              fieldFilterId += dateFilters.length;
            } else {
              fieldFilterId++;
              const criteria = new FilterCriteria();
              criteria.Id = fieldFilterId;
              criteria.Field = filterField;
              criteria.LogicalOperator = this.getLogicalOperator(fieldFilter.fieldName, fieldFilter.value);
              criteria.LinkOperator = FilterLinkOperatorEnum.And;
              const comparativeValue = this.getComparativeValue(fieldFilter.fieldName, fieldFilter.value);
              criteria.ComparativeValue = comparativeValue;

              fieldFilterCriterias.push(criteria);
            }
          }
        });
      }

      searchFilter.FilterItems = [{ Criterias: [...layoutFilterCriterias, ...(layoutState.searchFilters ?? []), ...fieldFilterCriterias] }];

      searchParams.Filter = searchFilter;
    }

    if (!setSorting) {
      searchParams.SortingSource = FaultSortingSourceEnum.None;
    } else {
      if (!layoutState.sortItems || layoutState.sortItems.length === 0) {
        const sortItemsParam = await this.colDefsService.getSortItems();
        layoutState.sortItems = sortItemsParam.LayoutSortItems.filter(x => x.Field?.DefaultName != null);
      }

      if (layoutState.sortItems && layoutState.sortItems.length > 0) {
        searchParams.SortingSource = FaultSortingSourceEnum.Passed;
        searchParams.Sorting = layoutState.sortItems.map(m => ({ DefaultName: m.Field.DefaultName, SortDirection: m.Direction, IsGroupSorting: m.IsGroupSorting }));
        console.warn('[mm-faults-loader] Using sortItems from layoutState:', layoutState.sortItems);
        console.warn('[mm-faults-loader] API Sorting payload:', searchParams.Sorting);

      } else {
        searchParams.SortingSource = FaultSortingSourceEnum.Layout;
      }
    }
    searchParams.AdditionalData = (onlySearchIds ? ['Defect_UniqueId', 'Defect_AttributeId', 'PlanPinText', 'Defect_CreatorId', 'Defect_ModifierId'] : ['Defect_UniqueId', 'Defect_AttributeId']);

    if (poolFilters.length > 0) {
      searchParams.AdditionalData.push('PoolsDefectsCount');
    }

    return searchParams;
  }

  private laodViewFaultPin(vf: ViewFault) {
    const lf = new LayoutFault({ id: vf.id, uid: vf.uid, isLocked: vf.isLocked });
    lf.data.Coordinates = [{ FileId: vf.data.PlanId, PosX: vf.data.PlanPositionX, PosY: vf.data.PlanPositionY }];
    this.loadFaultPin(vf, lf);
  }
  private loadFaultPin(vf: ViewFault, lf: LayoutFault) {
    let bpPin;
    if (vf.isNew) {
      bpPin = lf.data?.Pin;
    }
    else {
      bpPin = this.pinsLoaderService.getPinByFaultUid(lf.uid);
    }

    if (bpPin) {
      vf.pin = this.pinsLoaderService.convertToCoordinate(bpPin);
      vf.bpPin = bpPin;
    } else {
      vf.pin = lf.data?.Coordinates?.length > 0 ? lf.data?.Coordinates[0] : null;
      if (!vf.pin && lf.data?.PlanId && lf.data?.PlanPositionX && lf.data?.PlanPositionY) {
        const coordinate = new Coordinate();
        coordinate.FileId = lf.data?.PlanId;
        coordinate.PosX = lf.data?.PlanPositionX;
        coordinate.PosY = lf.data?.PlanPositionY;
        vf.pin = coordinate;
      }
    }
  }

  private async initFieldsUserFilterService(result: any ) {

    if (!this.platformModeStateService.isLocalMode) {

      if (result?.data && Array.isArray(result.data)) {
        const creatorIds: number[] = [];
        const modifierIds: number[] = [];
        result.data.forEach(fault => {
          if (fault.data?.CreatorId && !creatorIds.includes(fault.data.CreatorId)) {
            creatorIds.push(fault.data.CreatorId);
          }
          if (fault.data?.ModifierId && !modifierIds.includes(fault.data.ModifierId)) {
            modifierIds.push(fault.data.ModifierId);
          }
        });

        const fieldsUserFilterService = this.injector.get(FieldsUserFilterService, null);
        if (fieldsUserFilterService) {
          const currentFilterState = await firstValueFrom(this.filterService.state);
          const hasActiveFieldFilters = currentFilterState?.fieldFilters?.length > 0;

          if (!hasActiveFieldFilters) {
            fieldsUserFilterService.resetUserFilters();
          } else if (result.data.length === 0) {
            fieldsUserFilterService.showOnlyMeUser();
          } else if (creatorIds.length > 0 || modifierIds.length > 0) {
            fieldsUserFilterService.filterUsersBySearchResults(creatorIds, modifierIds);
          } else {
            fieldsUserFilterService.showOnlyMeUser();
          }
        }
      } else {
        const fieldsUserFilterService = this.injector.get(FieldsUserFilterService, null);
        if (fieldsUserFilterService) {
          fieldsUserFilterService.resetUserFilters();
        }
      }
    }
  }


  private async loadOfflineData(project: Project, structure: Structure, searchParams: FaultDataSearch): Promise<ViewFault[]> {

    const selecetdFaults = await firstValueFrom(this.selectionService.selectedItems);

    let offlineData = await this.faultService.getOfflineFaults(project.MyDocmaUid);
    if (this.platformModeStateService.isLocalMode) {
      offlineData = await this.mmOfflineFilterService.applyOfflineFilter(offlineData, { project: project, structure: structure }, searchParams);
    }
    if (this.offline) {
      if (structure && structure.Id !== project.Structure.Id) {
        this.projectStructures = (await firstValueFrom(this.projectLoaderService.structures.pipe(filter(s => s.loadState.status === LoadStatusEnum.Loaded)))).data;
        const loadedStructure = this.projectStructures[0].findById(structure.Id);
        offlineData = offlineData.filter(f => f.loadState.status === LoadStatusEnum.Error ||
          (f.data.Structure && f.data.Structure.Id && loadedStructure.findById(f.data.Structure.Id)));
      }
      offlineData = offlineData.map(f => {
        const vf = new ViewFault(f);
        vf.project = project;
        this.applySelectedState(vf, selecetdFaults);
        this.laodViewFaultPin(vf);
        //    vf.pin = f.data?.Coordinates?.length > 0 ? f.data?.Coordinates[0] : null;
        return vf;
      });

      if (this.currentFault && this.currentFault?.unsavedNew && this.loadedFaults.findIndex(x => x.uid === this.currentFault.uid) === -1) {
        // console.warn('0#ADDED CURRENT_FAUTLT AGAIN!!');
        // console.warn(this.currentFault);
        offlineData.splice(0, 0, this.currentFault);
      }
    }
    return (offlineData as ViewFault[]);
  }


  private createViewFault(project: Project, layoutState: LayoutFault, offlineState?: ViewFault): ViewFault {
    const vf = new ViewFault(offlineState);
    vf.project = project;
    if (!offlineState) {
      vf.id = layoutState.id;
      vf.uid = layoutState.uid;
    }
    vf.layoutState = layoutState;
    vf.isLocked =  layoutState.isLocked;
    if (!offlineState) {
        if (layoutState.data?.AttributeId) {
          //
          vf.attributeId = layoutState.data?.AttributeId;
          vf.data.Attribute = {Id: vf.attributeId};
        }

        vf.pinText = (layoutState.data.PlanPinText?.length > 25 ? layoutState.data.PlanPinText.slice(0, 25) + '...' : layoutState.data.PlanPinText);

    }
    if (!offlineState?.pin) {
        this.loadFaultPin(vf, layoutState);
    }


    if (vf.data) {
       vf.data.IsLocked = layoutState.isLocked;

    }

    return vf;


  }

  private async loadAllFaultIds(project: Project, structure: Structure, layoutState: LayoutStateModel,
    reloadFaultDetails: boolean = false,
    refreshByTileView: boolean = false,
    loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {

    this.onFaultIdsLoadingSubject.next(true);

    try {
      const structureId = structure && structure.Id ? structure.Id : project.Structure.Id;
      this.faultsLoadedGridNoFieldsCase = (layoutState.layout.Fields.length === 0 && this.toggledLeftViews.includes(HeaderToggleMenuEnum.gridView));


      this.loadedFaults = [];

      const selecetdFaults = await this.selectionService.selectedItems.pipe(first()).toPromise();
      const searchParams = await this.getApiSearchParams(structureId, this.layoutState, true, true);

      let offlineData = await this.loadOfflineData(project, structure,searchParams);

      if (this.offline) {

        this.loadedFaults = offlineData;
        await this.applyLocalSorting();
        await this.filterFaultsByTags(loadOptions);

        return;
      }

      if (!this.colDefsService.currentLayoutState && !!this.layoutState) {
        await this.colDefsService.applyLayoutState(this.layoutState);
      }

      const isTileView = (this.appContainerService.isMobileView ? true : (await this.personalizationService.getValue(PersonalizationKeysEnum.Fault_LeftToggleGroup))?.some(x => x === 'tileView'));

      let result: {
        data: LayoutFault[],
        poolsDefectsCount: { PoolId: number, DefectsCount: number }[]
      } = { data: [], poolsDefectsCount: [] };

      if (!this.platformModeStateService.isLocalMode) {

        // load ids from api if not local mode

        result = await firstValueFrom(iif(() => (
          (layoutState.layout.Fields || []).length > 0 || isTileView || refreshByTileView),
          this.faultDataService.searchFaults(searchParams).pipe(takeUntil(this.onLayoutStateChanged)),
          of<{ data: LayoutFault[], poolsDefectsCount: { PoolId: number, DefectsCount: number }[] }>(null)));

        await this.initFieldsUserFilterService(result);

        if (result?.poolsDefectsCount) {
          const poolsDefectCounts = result.poolsDefectsCount.map(x => ({ poolId: x.PoolId, newCount: x.DefectsCount }));
          this.poolCountChangedSubject.next(poolsDefectCounts);
        }
      }


      const newOfflineData: Array<ViewFault> = [];
      const otherOfflineData: Array<ViewFault> = [];
      const offlineDataMap= new Map<string, ViewFault>()
      offlineData.forEach(o => {
        if (o.changeState && o.isNew) {
          newOfflineData.push(o);
        } else {
          otherOfflineData.push(o);
        }
        offlineDataMap.set(o.uid, o);
      });

      this.loadedFaults = (result?.data || []) .map(f => {
          const foundOffline = offlineDataMap.get(f.uid);
          let vf = this.createViewFault(project, f, foundOffline);
          if (foundOffline && reloadFaultDetails) {
              this.onFaultRefreshed.next(vf);
          }
          this.applySelectedState(vf, selecetdFaults);
          return vf;
      });

      if (otherOfflineData.length && this.platformModeStateService.isLocalMode) {
        // Fault ids are not loaded from api in local mode,
        // add only downloaded faults to array
          this.loadedFaults.unshift(...otherOfflineData.map(f => {
            const vf = this.createViewFault(project,
              new LayoutFault({ loadState: new LoadState({ status: LoadStatusEnum.Loaded, data: {} }) }),
              f
            );
            this.applySelectedState(vf, selecetdFaults);
            return vf;
          }));
      }

      if (newOfflineData.length > 0) {

        this.loadedFaults.unshift(...newOfflineData.map(f => {
          const vf = this.createViewFault(project,
             new LayoutFault({ loadState: new LoadState({ status: LoadStatusEnum.Loaded, data: {} }) }),
             f);
          //TODO check
          //vf.pin = f.data.pin;
          this.applySelectedState(vf, selecetdFaults);
          return vf;
        }));
      }

      if (this.platformModeStateService.isLocalMode) {
        const poolsDefectCounts = await this.getLocalPoolCount(project);
        this.poolCountChangedSubject.next(poolsDefectCounts);
      }

      if (this.currentFault && this.currentFault?.unsavedNew && this.loadedFaults.findIndex(x => x.uid === this.currentFault.uid) === -1 && !this.platformModeStateService.isLocalMode) {
        // console.warn('1#ADDED CURRENT_FAUTLT AGAIN!!');
        // console.warn(this.currentFault);
        this.loadedFaults.splice(0, 0, this._currentFault);
      }

      await this.applyLocalSorting();

      await this.filterFaultsByTags(loadOptions);
    } finally {
      this.onFaultIdsLoadingSubject.next(false);
    }
  }

  private applySelectedState(vf: ViewFault, selectedFaults: ViewFault[]) {
    const foundIndex = selectedFaults.findIndex(s => this.selectionService.compareItems(vf, s));
    if (foundIndex > -1 && selectedFaults[foundIndex] !== vf) {
      selectedFaults[foundIndex] = vf;
    }
    vf.isChecked = foundIndex > -1;
  }

  private async applyLocalSorting(sortState?: TileSortState): Promise<void> {
    if (!this.platformModeStateService.isLocalMode && !this.offline) {
      return;
    }
    const state = sortState ?? this.faultsSortingService.getCurrentSortState();
    this.loadedFaults = await this.loadedFaultsSortingService.sortFaults(this.loadedFaults, state);
  }

  private hasFaultFilterTags(f: Fault): boolean {
    const tagNew = this.filter.getTag('fault-new');
    const tagChanged = this.filter.getTag('fault-changed');
    const tagDownloaded = this.filter.getTag('fault-downloaded');
    const tagSyncError = this.filter.getTag('fault-sync-error');
    const tagLoadError = this.filter.getTag('fault-load-error');
    let hasTags = true;
    if (tagNew) {
      hasTags = f.changeState && f.changeState.status === ChangeStatusEnum.New;
    }
    if (tagChanged && hasTags) {
      hasTags = f.changeState && f.changeState.status === ChangeStatusEnum.Changed;
    }
    if (tagDownloaded && hasTags) {
      hasTags = f.downloaded;
    }
    if (tagSyncError && hasTags) {
      hasTags = f.syncState && f.syncState.status === SyncStatusEnum.Error;
    }
    if (tagLoadError && hasTags) {
      hasTags = f.loadState && f.loadState.status === LoadStatusEnum.Error;
    }
    return hasTags;
  }


  private async filterFaultsByTags(loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {
    if (!this.filter) {
      return;
    }
    const tagNew = this.filter.getTag('fault-new');
    const tagChanged = this.filter.getTag('fault-changed');
    const tagDownloaded = this.filter.getTag('fault-downloaded');
    const tagSyncError = this.filter.getTag('fault-sync-error');
    const tagLoadError = this.filter.getTag('fault-load-error');
    let filteredFaults = (this.loadedFaults && this.loadedFaults.length > 0 ? [...this.loadedFaults] : []);
    if (tagNew) {
      filteredFaults = filteredFaults.filter(f => f.changeState && f.changeState.status === ChangeStatusEnum.New);
    }
    if (tagChanged) {
      filteredFaults = filteredFaults.filter(f => f.changeState && f.changeState.status === ChangeStatusEnum.Changed);
    }
    if (tagDownloaded) {
      filteredFaults = filteredFaults.filter(f => f.downloaded);
    }
    if (tagSyncError) {
      filteredFaults = filteredFaults.filter(f => f.syncState && f.syncState.status === SyncStatusEnum.Error);
    }
    if (tagLoadError) {
      filteredFaults = filteredFaults.filter(f => f.loadState && f.loadState.status === LoadStatusEnum.Error);
    }

    if (this.syncStateChanges) {
      await Promise.all(Object.keys(this.syncStateChanges).map(async f => {
        const fault = filteredFaults.find(x => x.uid === f);
        if (fault && this.syncStateChanges[f].syncState?.status && fault.syncState.status !== this.syncStateChanges[f].syncState?.status) {
          fault.syncState.status = this.syncStateChanges[f].syncState.status;
        }
      }));
    }

    // console.debug('faultsLoader.faults - ### plans set ### 01 - ' + filteredFaults?.length);

    this.faultsSubject.next({ loadedFaults: filteredFaults, loadOptions });

  }

  public loadAttachmentSource(fault: ViewFault, attachment: Attachment | number): Observable<AttachmentSource> {
    if (!fault) {
      throw new Error('Fault is not set');
    }
    let found: Attachment;
    if (typeof attachment === 'number') {
      const attachmentId = attachment;
      if (fault && fault.attachments) {
        found = fault.attachments.find(a => a.id === attachmentId);
      }
      if (!found) {
        fault.attachments = fault.attachments || [];
        found = new Attachment();
        found.id = attachmentId;
        fault.attachments.push(found);
      }
    } else {
      found = attachment;
    }
    let loadingType = AttachmentLoadingTypeEnum.FromServer;
    if (fault.downloaded && this.platformModeStateService.isLocalMode) {
      loadingType = AttachmentLoadingTypeEnum.FromDb;
    } else if (this.offline) {
      loadingType = found.changeState.status === ChangeStatusEnum.New ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.None;
    } else {
      loadingType = found.changeState.status === ChangeStatusEnum.New ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.FromServer;
    }

    return from(this.attachmentService.getAttachmentSource(found, false, undefined, fault.project.MyDocmaUid, loadingType));
  }

  public async loadAttachmentThumbnail(fault: ViewFault, attachment: Attachment | number, size?: { height: number, width: number }, force: boolean = false) {
    if (!fault) {
      throw new Error('Fault is not set');
    }

    let found: Attachment;
    if (typeof attachment === 'number') {
      const attachmentId = attachment;

      const faultAttachments = Object.fromEntries(fault.attachments?.map(x => [x.id, x]) ?? []);
      const activitiesAttachments = Object.fromEntries(fault.activities?.flatMap(x => x?.attachments ?? [])?.map(x => [x.id, x]) ?? []);

      if (faultAttachments.hasOwnProperty(attachmentId)) {
        found = faultAttachments[attachmentId];
      }
      else if (activitiesAttachments.hasOwnProperty(attachmentId)) {
        found = activitiesAttachments[attachmentId];
      }
      if (!found) {
        fault.attachments = fault.attachments || [];
        found = new Attachment();
        found.id = attachmentId;
        fault.attachments.push(found);
      }
    } else {
      found = attachment;
    }
    let loadingType = AttachmentLoadingTypeEnum.FromServer;
    if (fault.downloaded && this.platformModeStateService.isLocalMode) {
      loadingType = AttachmentLoadingTypeEnum.FromDb;
    } else if (this.offline) {
      loadingType = found.isNew ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.None;
    } else {
      loadingType = found?.isNew ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.FromServer;
    }

    return { Attachment: found, Source: await this.attachmentService.loadAttachmentThumbnail(fault.project.MyDocmaUid, found, size, loadingType, force) };
  }

  public async loadAttachmentPreview(fault: ViewFault, attachment: Attachment | number, size?: { height: number, width: number }, force: boolean = false) {
    if (!fault) {
      throw new Error('Fault is not set');
    }

    let found: Attachment;
    if (typeof attachment === 'number') {
      const attachmentId = attachment;
      if (fault && fault.attachments) {
        found = fault.attachments.find(a => a.id === attachmentId);
      }
      if (!found) {
        fault.attachments = fault.attachments || [];
        found = new Attachment();
        found.id = attachmentId;
        fault.attachments.push(found);
      }
    } else {
      found = attachment;
    }
    let loadingType = AttachmentLoadingTypeEnum.FromServer;
    if (fault.downloaded && this.platformModeStateService.isLocalMode) {
      loadingType = AttachmentLoadingTypeEnum.FromDb;
    } else if (this.offline) {
      loadingType = found.isNew ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.None;
    } else {
      loadingType = found.isNew ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.FromServer;
    }

    return { Attachment: found, Source: await this.attachmentService.loadAttachmentPreview(fault.project.MyDocmaUid, found, size, loadingType, force) };
  }

  public async loadLayoutFaults(faultsToLoad: ViewFault[], force: boolean = false, notifyNotInFilterMessage: boolean = false, loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {

    if (!faultsToLoad || faultsToLoad.length === 0) {
      this.loadLayoutFaultsOptionsSubject.next(loadOptions);
      return of([]);
    }
    const structureId = ((this.filter.structure && this.filter.structure.Id) ? this.filter.structure.Id : this.filter.project.Structure.Id);
    const layoutId = ((this.layoutState.layout && this.layoutState.layout.Id) ? this.layoutState.layout.Id : 0);

    const faultsById: { [id: number]: string } = Object.fromEntries(faultsToLoad.map(f => [f.id, f.uid]));

    let faultIds: number[] = [];
    if (!force) {
      faultsToLoad.forEach(vf => {
        if (vf.layoutState.loadState.status !== LoadStatusEnum.Loaded &&
          vf.layoutState.loadState.status !== LoadStatusEnum.Loading) {
          if (!this.connectivityService.isOfflineVal) {
            vf.layoutState.loadState.status = LoadStatusEnum.Loading;
          }
          if (!vf.isNew) {
            faultIds.push(vf.id);
          }
        }
      });
    } else {
      faultIds = faultsToLoad.map(f => f.id);
    }

    const searchParams = await this.getApiSearchParams(structureId, this.layoutState, false, false, force);

    searchParams.Ids = faultIds;
    if (faultIds.length !== 0 && searchParams?.LayoutId != +environment.appContainerConfig.capacitor.localLayoutId) {
      return of({
        layoutStateId: this.layoutState.id,
        faultIds: [...faultIds],
        params: searchParams,
        layoutFieldsCount: this.layoutState.layout.Fields.length
      }).pipe(
        tap(x => {
          this.loadingLayoutFaultsSubject.next(true);
        }),
        switchMap(request => this.faultDataService.loadLayoutFaults(request.params).pipe(map(response => ({ request, response })))),
        takeUntil(this.onLayoutStateChanged),
        map(r => {
          const data = r.response;
          const faultsToUpdate = [];
          const faultsToDelete = [];

          if (data && r.request.layoutStateId === this.layoutState.id) {

            const loadedFaultsDict = Object.fromEntries(data.map(x => [x.id, x]));
            let faultsNotInFilter = r.request.faultIds.some(f => !loadedFaultsDict.hasOwnProperty(f));
            if (faultsNotInFilter) {
              this.selectionService.stopSelectionMode();
              faultsToDelete.push(...r.request.faultIds.filter(f => !loadedFaultsDict.hasOwnProperty(f)).map(x => new Fault({ uid: faultsById[x] })));
            }

            if ((data?.length ?? 0) > 0 && (r.request.layoutFieldsCount > 0 || this.toggledLeftViews?.includes(HeaderToggleMenuEnum.tileView))) {
              const loaded = this.refreshViewFaultData(data, !force)
              faultsToUpdate.push(...loaded);
            }

            if (notifyNotInFilterMessage && faultsNotInFilter) {
              this.mmFaultsCountService.removeNewFaults(1);
              const messageKey = (r.request.layoutFieldsCount > 0 ? 'MM.fault.notInFilter' : 'MM.fault.notInLayout');
              this.translateService.get(messageKey).subscribe(t => this.notificationService.info(t));
            }

            if (faultsToDelete.length > 0) {
              this.removeFaults(faultsToDelete, true);
            }
            // this.faultService.validateChangeState(this.project.MyDocmaUid, null, faultsToUpdate);

            this.layoutFaultsSubject.next(faultsToUpdate);
            this.loadLayoutFaultsOptionsSubject.next(loadOptions);

            return faultsToUpdate;
          } else {
            return [];
          }
        }),
        finalize(() => this.loadingLayoutFaultsSubject.next(false))
      );
    } else {
      this.loadLayoutFaultsOptionsSubject.next(loadOptions);
      return of([]);
    }
  }

  public loadFaults(faultsToLoad: ViewFault[], force: boolean = false, additionalInformations: number = 11, cancelation?: Observable<any>): Observable<ViewFault[]> {
    if (this.offline) {
      return of(faultsToLoad);
    }
    setTimeout(() => this.loadingFaultsSubject.next(true), 0);

    if (!!this.dwActive) {
      additionalInformations += 4;
    }

    let structureId = -1;
    this.viewService.currentProject$.pipe(tap(x => structureId = x?.Structure?.Id)).toPromise();

    const res = from(this.faultService.loadFaults(this.filter.project.MyDocmaUid, structureId, faultsToLoad, force, additionalInformations, cancelation))
      .pipe(
        map(faults => faults as ViewFault[]),
        finalize(() => setTimeout(() => {
          this.loadingFaultsSubject.next(false);
        }, 250))
      );

    res.subscribe();

    return res;
  }

  private refreshViewFaultData(layoutFaults: LayoutFault[], markFaultAsLoaded = true) {
    if (!layoutFaults) {
      return null;
    }

    const loadedFaultsDict = Object.fromEntries(this.faultsSubject.value.loadedFaults.map(x => [x.uid, x]));

    return layoutFaults.map(layoutFault => {
      if (loadedFaultsDict.hasOwnProperty(layoutFault.uid)) {
        const found = loadedFaultsDict[layoutFault.uid];
        found.firstLoad = found.layoutState.loadState.status !== LoadStatusEnum.Loaded;
        found.layoutState.loadState = new LoadState({ data: layoutFault.data, status: LoadStatusEnum.Loaded });
        if (!markFaultAsLoaded) {
          found.firstLoad = false;
        }
        if (!found.uid && found.layoutState.uid) {
          found.uid = found.layoutState.uid;
        }

        /* TODO MMW-5194 - OPTIMIZE UPDATE MYDOCMA-GRID */
        this.layoutFaultSubject.next(found);

        return found;
      }
    });
  }

  private async removeGroupFaultActivites(activity: Activity, groupDelete: boolean, modifiedOn: Date, modifier: User) {
    await Promise.all(this.loadedFaults.map(async fault => {
      let changed = false;

      const activities = fault.activities
        .filter(act => (groupDelete && act?.data?.Group?.UniqueId === activity.group.uid) || (!groupDelete && act?.data.Id === activity.id));

      const updFaultsCountActivities = fault.activities
        .filter(act => (!groupDelete && act?.data.Id !== activity.id && act?.data?.Group?.UniqueId === activity.group?.uid));

      await Promise.all(activities.map(async act => {
        fault.removeActivity(act);
        changed = true;
      }));
      await Promise.all(updFaultsCountActivities.map(async act => {
        act.data.FaultsCount--;
        changed = true;
      }));

      if (changed) {
        fault.data.ModifiedOn = modifiedOn;
        fault.data.Modifier = modifier;
        fault.loadState.data.ModifiedOn = modifiedOn;
        fault.loadState.data.Modifier = modifier;
        if ((activities?.length ?? 0) > 0) {
          this.activitiesDeletedSubject.next(activities);
        }
      }
    }));
  }

  async discardLoadedFaults(faultUids: string[]) {
    const loadedFaultsDict = Object.fromEntries(this.loadedFaults.map(x => [x.uid, x]));
    for (const faultUid of faultUids) {
      if (loadedFaultsDict.hasOwnProperty(faultUid)) {
        loadedFaultsDict[faultUid].loadState = new LoadState({ status: LoadStatusEnum.NotLoaded });
      }
    }
    // this.loadedFaults.filter(x => faultUids.findIndex(f => x.uid === f) !== -1).forEach(fault => {
    //   fault.loadState = new LoadState({status: LoadStatusEnum.NotLoaded});
    // });
  }

  public async ChangeActivityAttachment(attachment: Attachment, activity: Activity) {
    Promise.all(this.loadedFaults.map(async f => {
      if (activity?.fault && f.uid !== activity.fault.uid) {
        f.activities.forEach(act => {
          if (act?.group && act.group?.uid === activity.group?.uid && act.uid !== activity.uid) {
            const foundAtt = act.attachments.find(at => at.uid === attachment.uid);
            if (!foundAtt) {
              act.attachments.push(new Attachment(attachment));
            } else {
              Object.assign(foundAtt.data, attachment.changeState.data);
            }
          }
        });
      }
    }));
  }

  public async AddActivityAttachments(attachments: Attachment[], activity: Activity) {
    Promise.all(this.loadedFaults.map(async f => {
      if (activity?.fault && f.uid !== activity.fault.uid) {
        f.activities.forEach(act => {
          if (act?.group && act.group?.uid === activity.group?.uid && act.uid !== activity.uid) {
            const newAttachments = attachments.filter(at => act.attachments.findIndex(f => f.uid === at.uid) === -1);
            if (newAttachments) {
              newAttachments.forEach(att => {
                act.attachments.push(att);
              });
            }
          }
        });
      }
    }));
  }

  public async DeleteActivityAttachment(attachment: Attachment, activity: Activity, modifiedOn: Date, modifier: User) {
    Promise.all(this.loadedFaults.map(async f => {
      let changed = false;
      if (activity?.fault && f.uid !== activity.fault.uid) {
        f.activities.forEach(act => {
          if (act?.group && act.group?.uid === activity.group?.uid && act.uid !== activity.uid) {
            const foundAtt = act.attachments.findIndex(at => at.uid === attachment.uid);
            if (foundAtt !== -1) {
              act.attachments.splice(foundAtt, 1);
              changed = true;
            }
          }
        });
      }

      if (changed) {
        f.data.ModifiedOn = modifiedOn;
        f.data.Modifier = modifier;
        f.loadState.data.ModifiedOn = modifiedOn;
        f.loadState.data.Modifier = modifier;
      }
    }));
  }

  public async ApplyMultiActivityChanges(param: { multiActivity: MultiActivity, changes: ActivityData, patchMode: ActivityPatchModeEnum }) {
    if (param.multiActivity.entities) {

      const entities = (param.patchMode === ActivityPatchModeEnum.MultiActivity_Single ? param.multiActivity.entities.filter(e => e.IsInitActivity) : param.multiActivity.entities);
      const groupEntities = (param.patchMode === ActivityPatchModeEnum.MultiActivity_Single ? param.multiActivity.entities.filter(e => !e.IsInitActivity) : []);

      entities.map(async e => {
        const fault = this.loadedFaults.find(f => f.uid === e.Fault.uid);
        if (fault) {
          const activity = fault.activities.find(a => a.uid === e.Activity.uid);
          if (activity) {
            if (!activity.id && activity.id !== e.Activity.id) {
              activity.id = e.Activity.id;
            }
            Object.keys(param.changes).forEach(key => {
              if (activity.data[key] !== param.changes[key]) {
                // activity.data[key] = param.changes[key];
              }
            });
          }
          else {
            fault.activities.push(e.Activity);
          }
        }
      });

      groupEntities.map(async e => {
        const fault = this.loadedFaults.find(f => f.uid === e.Fault.uid);
        if (fault) {
          const activity = fault.activities.find(a => a.uid === e.Activity.uid);
          if (activity) {
            activity.data.FaultsCount--;
          }
        }
      });

    }
  }

  public emitPoolCountUpdated(poolCountChanges: { poolId: number, newCount?: number }[]): void {
    this.poolCountChangedSubject.next(poolCountChanges);
  }

  public setShouldHideFaultCallback(callback: (fault: ViewFault) => boolean): void {
    this.shouldHideFaultCallback = callback;
  }

  public reevaluateHiddenFault(fault: ViewFault): void {

    const shouldHide = this.shouldHideFaultCallback ? this.shouldHideFaultCallback(fault) : false;
    const faultInLoadedList = this.loadedFaults.find(f => f.uid === fault.uid);
    const faultInSubjectList = this.faultsSubject.value.loadedFaults.find(f => f.uid === fault.uid);
    const isCurrentlyHidden = !faultInLoadedList && !faultInSubjectList;
    const isCurrentlyVisible = !isCurrentlyHidden;

    if (!shouldHide && isCurrentlyHidden) {
      this.loadedFaults.splice(0, 0, fault);
      this.faultsSubject.value.loadedFaults.splice(0, 0, fault);
      this.onFaultAdded.emit(fault);
      this.mmFaultsCountService.addNewFaults(1);
    } else if (shouldHide && isCurrentlyVisible) {
      const faultToRemove = faultInLoadedList || faultInSubjectList || fault;
      this.removeFaults([faultToRemove]);
    }
  }

  public removeFaultFromView(faultToRemove: ViewFault): void {
    if (!faultToRemove) { return; }

    const currentData = this.faultsSubject.getValue();
    const currentFaults = currentData.loadedFaults;
    const index = currentFaults.findIndex(f => (faultToRemove.id && faultToRemove.id === f.id) || (faultToRemove.uid && faultToRemove.uid === f.uid));

    if (index > -1) {
      const removedFaults = [currentFaults[index]];
      const newFaults = currentFaults.filter((f, i) => i !== index);

      const foundIndexInLoaded = this.loadedFaults.findIndex(f => (faultToRemove.id && faultToRemove.id === f.id) || (faultToRemove.uid && faultToRemove.uid === f.uid));
      if (foundIndexInLoaded > -1) {
        this.loadedFaults.splice(foundIndexInLoaded, 1);
      }

      this.faultsSubject.next({ ...currentData, loadedFaults: newFaults });
      this.onFaultsRemoved.emit(removedFaults);
    }
  }

  private getFieldDictKey(fieldName: string): string {
    return FIELD_DICT_MAPPING[fieldName] || fieldName;
  }

  private getLogicalOperator(fieldName: string, value: string): FilterOperatorEnum {
    if (value === EMPTY_FILTER_MARKER) {
      return FilterOperatorEnum.Empty;
    }

    if (!value || value === '') {
      return FilterOperatorEnum.Empty;
    }

    if (fieldName === FieldMappingEnum.Creator ||
      fieldName === FieldMappingEnum.Modifier ||
      fieldName === FieldMappingEnum.AttributeGroupImage ||
      fieldName === FieldMappingEnum.AttributeName1 ||
      fieldName === FieldMappingEnum.CompanyName1 ||
      fieldName === FieldMappingEnum.CraftName1 ||
      fieldName === FieldMappingEnum.OrderName1 ||
      fieldName === FieldMappingEnum.DelayGroupSCId ||
      fieldName === FieldMappingEnum.DelayGroupCTId ||
      fieldName === FieldMappingEnum.LastActivitySCStatusName1 ||
      fieldName === FieldMappingEnum.LastActivityCTStatusName1 ||
      fieldName === FieldMappingEnum.ResponsibleCOCompany ||
      fieldName === FieldMappingEnum.ResponsibleCO ||
      fieldName === FieldMappingEnum.ResponsibleSCCompany ||
      fieldName === FieldMappingEnum.ResponsibleSC ||
      fieldName === FieldMappingEnum.ResponsibleCTCompany ||
      fieldName === FieldMappingEnum.ResponsibleCT ||
      fieldName === FieldMappingEnum.LastActivitySCStatusGroupSC ||
      fieldName === FieldMappingEnum.LastActivityCOStatusGroupSC ||
      fieldName === FieldMappingEnum.LastActivityCOStatusGroupCT ||
      fieldName === FieldMappingEnum.LastActivityCTStatusGroupCT ||
      fieldName === FieldMappingEnum.Classification1Name1 ||
      fieldName === FieldMappingEnum.Classification2Name1 ||
      fieldName === FieldMappingEnum.Classification3Name1 ||
      fieldName === FieldMappingEnum.Classification4Name1 ||
      fieldName === FieldMappingEnum.ProjectSpecificName1 ||
      fieldName === FieldMappingEnum.ProjectSpecific2Name1) {
      return FilterOperatorEnum.Equal;
    }

    return FilterOperatorEnum.Like;
  }

  private getComparativeValue(fieldName: string, value: string): string {
    if (fieldName === FieldMappingEnum.CreatedOn || fieldName === FieldMappingEnum.ModifiedOn) {
      return value;
    }

    if ((fieldName === FieldMappingEnum.AttributeGroupImage || fieldName === FieldMappingEnum.AttributeName1) && value === 'EMPTY') {
      return null;
    }

    if (value === EMPTY_FILTER_MARKER) {
      return null;
    }

    return value;
  }


  // private applyDateFilters(searchParams: FaultDataSearch, filterModel: MmFilterModel): void {
  //   if (!filterModel.fieldFilters) {
  //     return;
  //   }

  //   const modifiedOnFilter = filterModel.fieldFilters.find(f => f.fieldName === FieldMappingEnum.ModifiedOn);

  //   if (modifiedOnFilter) {
  //     const dateRange = this.convertDateFilterToRange(modifiedOnFilter.value);

  //     if (dateRange.startDate) {
  //       searchParams.ModifiedAfter = dateRange.startDate;
  //     }
  //     if (dateRange.endDate) {
  //       searchParams.ModifiedBefore = dateRange.endDate;
  //     }
  //   }
  // }

  private convertDateFilterToRange(filterValue: string): { startDate?: Date; endDate?: Date } {
    if (filterValue && filterValue.includes('|')) {
      const [startStr, endStr] = filterValue.split('|').map(s => s.trim());
      return {
        startDate: this.parseGermanDate(startStr),
        endDate: this.parseGermanDate(endStr)
      };
    }

    if (filterValue && filterValue.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
      const singleDate = this.parseGermanDate(filterValue);
      if (singleDate) {
        const endOfDay = new Date(singleDate);
        endOfDay.setHours(23, 59, 59, 999);
        return {
          startDate: singleDate,
          endDate: endOfDay
        };
      }
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const FIRST_DAY_OF_MONTH = 1;
    const JANUARY = 0;
    const DECEMBER = 11;
    const LAST_DAY_OF_DECEMBER = 31;
    const END_OF_DAY_HOURS = 23;
    const END_OF_DAY_MINUTES = 59;
    const END_OF_DAY_SECONDS = 59;
    const END_OF_DAY_MILLISECONDS = 999;

    switch (filterValue) {
      case DateFilterOptionsEnum.thisMonth:
        return {
          startDate: new Date(currentYear, currentMonth, FIRST_DAY_OF_MONTH),
          endDate: new Date(currentYear, currentMonth + 1, 0, END_OF_DAY_HOURS, END_OF_DAY_MINUTES, END_OF_DAY_SECONDS, END_OF_DAY_MILLISECONDS)
        };

      case DateFilterOptionsEnum.lastMonth:
        const lastMonth = currentMonth === 0 ? DECEMBER : currentMonth - 1;
        const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
        return {
          startDate: new Date(lastMonthYear, lastMonth, FIRST_DAY_OF_MONTH),
          endDate: new Date(lastMonthYear, lastMonth + 1, 0, END_OF_DAY_HOURS, END_OF_DAY_MINUTES, END_OF_DAY_SECONDS, END_OF_DAY_MILLISECONDS)
        };

      case DateFilterOptionsEnum.thisYear:
        return {
          startDate: new Date(currentYear, JANUARY, FIRST_DAY_OF_MONTH),
          endDate: new Date(currentYear, DECEMBER, LAST_DAY_OF_DECEMBER, END_OF_DAY_HOURS, END_OF_DAY_MINUTES, END_OF_DAY_SECONDS, END_OF_DAY_MILLISECONDS)
        };

      case DateFilterOptionsEnum.lastYear:
        return {
          startDate: new Date(currentYear - 1, JANUARY, FIRST_DAY_OF_MONTH),
          endDate: new Date(currentYear - 1, DECEMBER, LAST_DAY_OF_DECEMBER, END_OF_DAY_HOURS, END_OF_DAY_MINUTES, END_OF_DAY_SECONDS, END_OF_DAY_MILLISECONDS)
        };

      default:
        return {};
    }
  }

  private parseGermanDate(dateStr: string): Date {
    if (!dateStr) { return null; }

    const [day, month, year] = dateStr.split('.').map(n => parseInt(n, 10));
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private createDateFilterCriterias(fieldFilter: FieldFilter, filterField: Field, startId: number): FilterCriteria[] {
    const dateRange = this.convertDateFilterToRange(fieldFilter.value);

    if (dateRange.startDate && dateRange.endDate) {
      const betweenCriteria = new FilterCriteria();
      betweenCriteria.Id = startId;
      betweenCriteria.Field = filterField;
      betweenCriteria.LogicalOperator = FilterOperatorEnum.Between;
      betweenCriteria.LinkOperator = FilterLinkOperatorEnum.And;
      betweenCriteria.ComparativeValue = formatDateToYMD(dateRange.startDate);
      betweenCriteria.ComparativeValue2 = formatDateToYMD(dateRange.endDate);
      return [betweenCriteria];
    }
    else if (dateRange.startDate && !dateRange.endDate) {
      const startCriteria = new FilterCriteria();
      startCriteria.Id = startId;
      startCriteria.Field = filterField;
      startCriteria.LogicalOperator = FilterOperatorEnum.GreaterEqual;
      startCriteria.LinkOperator = FilterLinkOperatorEnum.And;
      startCriteria.ComparativeValue = formatDateToYMD(dateRange.startDate);
      return [startCriteria];
    }
    else if (!dateRange.startDate && dateRange.endDate) {
      const endCriteria = new FilterCriteria();
      endCriteria.Id = startId + 1;
      endCriteria.Field = filterField;
      endCriteria.LogicalOperator = FilterOperatorEnum.LessEqual;
      endCriteria.LinkOperator = FilterLinkOperatorEnum.And;
      endCriteria.ComparativeValue = formatDateToYMD(dateRange.endDate);
      return [endCriteria];
    }
    return [];
  }


  public async loadOfflineFault(faultsToLoad: ViewFault) {
    await this.faultService.loadOfflineFault(this.project.MyDocmaUid, faultsToLoad.uid);
  }

  public async getLocalPoolCount(project: Project): Promise<{ poolId: number; newCount: number }[]> {
    const pools = await firstValueFrom(this.downloadService.downloadPools(project));
    const poolsDefectCounts: { poolId: number; newCount: number }[] = pools.map(pool => {
      const poolId = pool.Id;
      const count = this.loadedFaults.filter(f => Array.isArray(f.data?.PoolIds) && f.data.PoolIds.includes(poolId)).length;
      return { poolId, newCount: count };
    });
    return poolsDefectCounts;
  }

}
